// Funnel event names — single source of truth.
// Keep names snake_case, past-tense for completed actions, present-tense for views/starts.
export const AnalyticsEvent = {
  // App lifecycle
  AppOpened: 'app_opened',

  // Signup funnel
  SignupStarted: 'signup_started',
  SignupSubmitted: 'signup_submitted',
  SignupFailed: 'signup_failed',
  EmailVerificationSent: 'email_verification_sent',
  EmailVerified: 'email_verified',
  PhoneVerificationStarted: 'phone_verification_started',
  PhoneVerified: 'phone_verified',

  // 2FA / TOTP funnel
  SecurityChooserShown: 'security_chooser_shown',
  SecurityChooserProceeded: 'security_chooser_proceeded',
  SecurityChooserSkipped: 'security_chooser_skipped',
  TotpSetupShown: 'totp_setup_shown',
  TotpSetupSkipped: 'totp_setup_skipped',
  TotpSetupCompleted: 'totp_setup_completed',
  TotpVerificationShown: 'totp_verification_shown',
  TotpVerificationFailed: 'totp_verification_failed',
  TotpVerificationSucceeded: 'totp_verification_succeeded',

  // Biometric
  BiometricPromptShown: 'biometric_prompt_shown',
  BiometricEnabled: 'biometric_enabled',
  BiometricSkipped: 'biometric_skipped',

  // Login funnel
  LoginStarted: 'login_started',
  LoginSucceeded: 'login_succeeded',
  LoginFailed: 'login_failed',

  // Onboarding funnel
  OnboardingStarted: 'onboarding_started',
  OnboardingStepCompleted: 'onboarding_step_completed',
  OnboardingCompleted: 'onboarding_completed',
  OnboardingSkipped: 'onboarding_skipped',

  // AI onboarding (Orion) funnel — baseline instrumentation before Phase 2
  // moves Orion to post-dashboard. Needed to measure current drop-off.
  AiOnboardingWelcomeShown: 'ai_onboarding_welcome_shown',
  AiOnboardingModeSelected: 'ai_onboarding_mode_selected',
  AiOnboardingTutorialShown: 'ai_onboarding_tutorial_shown',
  AiOnboardingChatStarted: 'ai_onboarding_chat_started',
  AiOnboardingChatCompleted: 'ai_onboarding_chat_completed',
  AiOnboardingPrioritiesShown: 'ai_onboarding_priorities_shown',
  AiOnboardingPrioritiesAccepted: 'ai_onboarding_priorities_accepted',
  AiOnboardingPaused: 'ai_onboarding_paused',
  AiOnboardingSkipped: 'ai_onboarding_skipped',

  // Subscription funnel — baseline instrumentation before Phase 3 moves
  // subscription out of the pre-dashboard blocking position.
  SubscriptionPromptShown: 'subscription_prompt_shown',
  SubscriptionTierSelected: 'subscription_tier_selected',
  SubscriptionPurchaseStarted: 'subscription_purchase_started',
  SubscriptionPurchaseSucceeded: 'subscription_purchase_succeeded',
  SubscriptionPurchaseFailed: 'subscription_purchase_failed',
  SubscriptionSkipped: 'subscription_skipped',
  TrialStarted: 'trial_started',
  TrialAutoStartedFromWizard: 'trial_auto_started_from_wizard',
  TrialConverted: 'trial_converted',

  // New wizard (Phase 1+) — wired in later phases but declared here so
  // the single-source-of-truth events file stays the only place names live.
  WizardStepShown: 'wizard_step_shown',
  WizardStepCompleted: 'wizard_step_completed',
  WizardAnswerRecorded: 'wizard_answer_recorded',
  WizardSkipped: 'wizard_skipped',
  WizardCompleted: 'wizard_completed',
  PrioritiesRevealShown: 'priorities_reveal_shown',
  // Successor soft-ask (wizard step 6) — renamed from Keyholder* when
  // the wizard's final ask pivoted from keyholder to successor
  SuccessorSoftAskShown: 'successor_soft_ask_shown',
  SuccessorSoftAskSubmitted: 'successor_soft_ask_submitted',
  SuccessorSoftAskSkipped: 'successor_soft_ask_skipped',
  LegalZoomAffiliateClicked: 'legalzoom_affiliate_clicked',
  PrioritiesRevealTaskTapped: 'priorities_reveal_task_tapped',

  // Phase 5 — v0 → v1 migration prompt (dashboard banner for existing users)
  MigrationPromptShown: 'migration_prompt_shown',
  MigrationPromptAccepted: 'migration_prompt_accepted',
  MigrationPromptDismissed: 'migration_prompt_dismissed',

  // First-value / activation
  FirstDashboardView: 'first_dashboard_view',
  FirstFeatureUsed: 'first_feature_used',

  // Generic
  ScreenViewed: 'screen_viewed',
  ButtonTapped: 'button_tapped',
} as const;

export type AnalyticsEventName = typeof AnalyticsEvent[keyof typeof AnalyticsEvent];
